import { useEffect, useRef } from 'react';

interface UseToggleShortcutOptions {
  /** When false, the listener is not attached and the shortcut is inert. */
  enabled: boolean;
  /** Called when the shortcut (Ctrl/Cmd + Shift + .) is pressed. */
  onToggle: () => void;
}

/**
 * Registers a global `keydown` listener for `Ctrl/Cmd + Shift + .` that invokes
 * `onToggle`. The handler matches on the physical key code (`Period`) so it is
 * keyboard-layout stable, runs in the capture phase, and `preventDefault`s an
 * exact match so it wins over any page-level handler.
 *
 * `onToggle` is read through a ref so the listener is registered once per
 * `enabled` change rather than on every render.
 */
const useToggleShortcut = ({
  enabled,
  onToggle
}: UseToggleShortcutOptions): void => {
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      const primaryModifier = event.ctrlKey || event.metaKey;
      if (
        primaryModifier &&
        event.shiftKey &&
        !event.altKey &&
        event.code === 'Period'
      ) {
        event.preventDefault();
        onToggleRef.current();
      }
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => {
      window.removeEventListener('keydown', handler, { capture: true });
    };
  }, [enabled]);
};

export default useToggleShortcut;
